import { type ReactNode } from 'react';
import { Table as MantineTable, Group, Text } from '@mantine/core';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { colors } from '../../theme/tokens';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';

export type SortDirection = 'asc' | 'desc' | null;

export interface TableColumn<TRow> {
  key: string;
  header: ReactNode;
  /** Cell renderer. Receives the row + row index. */
  cell: (row: TRow, index: number) => ReactNode;
  width?: number | string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface TableProps<TRow> {
  columns: ReadonlyArray<TableColumn<TRow>>;
  rows: ReadonlyArray<TRow>;
  /** Stable row key resolver. */
  rowKey: (row: TRow, index: number) => string;
  /** Dense row padding — used by audit logs + range-vm grids. */
  dense?: boolean;
  /** Zebra striping. */
  zebra?: boolean;
  /** Sticky header — required for any table inside a scroll container. */
  stickyHeader?: boolean;
  /** Active sort. `key` matches a column key. */
  sort?: { key: string; direction: SortDirection };
  onSortChange?: (next: { key: string; direction: SortDirection }) => void;
  /** Loading state — replaces the rows with skeletons. */
  loading?: boolean;
  /** Number of skeleton rows to show while loading. */
  skeletonRows?: number;
  /** Custom empty-state slot. If omitted, a default placeholder renders. */
  emptyState?: ReactNode;
  /**
   * Makes the WHOLE row activate (click, Enter, Space) instead of only whatever
   * link a single cell happens to contain. Rows become `tabIndex=0` with
   * `role="link"` so the hover affordance stops lying.
   */
  onRowClick?: (row: TRow, index: number) => void;
  /** Accessible label per row, used when `onRowClick` is set. */
  rowLabel?: (row: TRow, index: number) => string;
}

/**
 * Table — dense / zebra / sticky-header data table primitive.
 *
 * Composes Mantine `Table`. Sortable column headers cycle null → asc → desc.
 * Loading state replaces the body with skeleton rows; empty state replaces
 * the body when `rows.length === 0`.
 */
export function Table<TRow>({
  columns,
  rows,
  rowKey,
  dense = false,
  zebra = true,
  stickyHeader = true,
  sort,
  onSortChange,
  loading = false,
  skeletonRows = 5,
  emptyState,
  onRowClick,
  rowLabel,
}: TableProps<TRow>) {
  const verticalSpacing = dense ? 4 : 10;
  const horizontalSpacing = dense ? 8 : 12;

  const handleSort = (col: TableColumn<TRow>) => {
    if (!col.sortable || !onSortChange) return;
    const isActive = sort?.key === col.key;
    const next: SortDirection = !isActive
      ? 'asc'
      : sort?.direction === 'asc'
        ? 'desc'
        : sort?.direction === 'desc'
          ? null
          : 'asc';
    onSortChange({ key: col.key, direction: next });
  };

  return (
    <div
      style={{
        border: '1px solid var(--c-bd)',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: 'rgb(var(--c-card))',
      }}
    >
      <MantineTable
        striped={zebra}
        highlightOnHover
        withTableBorder={false}
        withColumnBorders={false}
        stickyHeader={stickyHeader}
        verticalSpacing={verticalSpacing}
        horizontalSpacing={horizontalSpacing}
        styles={{
          table: {
            backgroundColor: 'rgb(var(--c-card))',
            // SYS-2: was #e4e4e7 (near-white → invisible on the light-mode card).
            // Theme-aware body text; inherits the app sans (dropped stray IBM Plex).
            color: 'rgb(var(--c-txt))',
            fontSize: 13,
          },
          th: {
            backgroundColor: 'rgb(var(--c-panel))',
            color: 'rgb(var(--c-txt-2))',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '1px solid var(--c-bd)',
          },
          tr: {
            borderColor: 'var(--c-bd)',
          },
        }}
      >
        <MantineTable.Thead>
          <MantineTable.Tr>
            {columns.map((col) => {
              const isActiveSort = sort?.key === col.key;
              const direction = isActiveSort ? sort?.direction : null;
              const label =
                typeof col.header === 'string' ? col.header : col.key;
                const inner = (
                  <Group gap={6} wrap="nowrap" justify={col.align === 'right' ? 'flex-end' : 'flex-start'}>
                    <span>{col.header}</span>
                    {col.sortable ? (
                      direction === 'asc' ? (
                        <ArrowUp size={12} color={colors.neon} />
                      ) : direction === 'desc' ? (
                        <ArrowDown size={12} color={colors.neon} />
                      ) : (
                        <ArrowUpDown size={12} color="rgb(var(--c-txt-muted))" />
                      )
                    ) : null}
                  </Group>
                );
                return (
                <MantineTable.Th
                  key={col.key}
                  style={{ width: col.width, textAlign: col.align ?? 'left' }}
                  // A11y: `aria-sort` must be "none" (not undefined) on an
                  // inactive sortable column so AT announces it as sortable.
                  aria-sort={
                    !col.sortable
                      ? undefined
                      : isActiveSort && direction === 'asc'
                        ? 'ascending'
                        : isActiveSort && direction === 'desc'
                          ? 'descending'
                          : 'none'
                  }
                >
                  {col.sortable ? (
                    // A real <button>, so the header is keyboard-operable and
                    // announces the column + current direction.
                    <button
                      type="button"
                      onClick={() => handleSort(col)}
                      aria-label={`Sort by ${label}, currently ${
                        isActiveSort && direction ? `${direction}ending` : 'unsorted'
                      }`}
                      style={{
                        all: 'unset',
                        cursor: 'pointer',
                        display: 'block',
                        width: '100%',
                        font: 'inherit',
                        color: 'inherit',
                      }}
                    >
                      {inner}
                    </button>
                  ) : (
                    inner
                  )}
                </MantineTable.Th>
              );
            })}
          </MantineTable.Tr>
        </MantineTable.Thead>

        <MantineTable.Tbody>
          {loading ? (
            Array.from({ length: skeletonRows }).map((_, rIdx) => (
              <MantineTable.Tr key={`sk-${rIdx}`}>
                {columns.map((col) => (
                  <MantineTable.Td key={col.key}>
                    <Skeleton height={10} width="80%" />
                  </MantineTable.Td>
                ))}
              </MantineTable.Tr>
            ))
          ) : rows.length === 0 ? (
            <MantineTable.Tr>
              <MantineTable.Td colSpan={columns.length} style={{ padding: 0 }}>
                {emptyState ?? (
                  <EmptyState
                    title="No rows"
                    description="There's nothing to show here yet."
                  />
                )}
              </MantineTable.Td>
            </MantineTable.Tr>
          ) : (
            rows.map((row, index) => (
              <MantineTable.Tr
                key={rowKey(row, index)}
                {...(onRowClick
                  ? {
                      role: 'link',
                      tabIndex: 0,
                      'aria-label': rowLabel?.(row, index),
                      style: { cursor: 'pointer' },
                      onClick: () => onRowClick(row, index),
                      onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row, index);
                        }
                      },
                    }
                  : {})}
              >
                {columns.map((col) => (
                  <MantineTable.Td
                    key={col.key}
                    style={{ textAlign: col.align ?? 'left' }}
                  >
                    {col.cell(row, index)}
                  </MantineTable.Td>
                ))}
              </MantineTable.Tr>
            ))
          )}
        </MantineTable.Tbody>
      </MantineTable>
      <Text size="xs" c="dimmed" px="md" py={4} style={{ borderTop: '1px solid var(--c-bd)' }}>
        {loading ? 'Loading…' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}
      </Text>
    </div>
  );
}
